import React, { useState } from 'react';
import { Link, useLocation } from '@reach/router';
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers } from 'formik';
import { SubscribeApi } from 'api/subscribeApi';
import { SubscribeParser } from 'pages/subscribe/models';
import FormTextField from 'components/formComponents/FormTextField';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSelectedPackages,
  selectShoolDetails,
  setRegistrationToken,
} from 'reducers/misc';
import {
  AppPath,
  PASSWORD_MATCH_POLICY_ERROR_MESSAGE,
  PASSWORD_REGEX,
  PUBLIC_SITE_URL,
} from 'appConstants';
import { FreeTrialSubscriptionApi } from 'api/freeTrialSubscriptionApi';
import { ErrorM } from 'components/formComponents/ErrorM';

type ProfileDetailsFormProps = {
  onSubmit: () => void;
};

const ProfileDetailsForm: React.FC<ProfileDetailsFormProps> = ({
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const schoolDetails = useSelector(selectShoolDetails);
  const selectedPackages = useSelector(selectSelectedPackages);

  const location = useLocation();
  const pathName = location.pathname;

  const [freeTrialPage, setFreeTrialPage] = useState<boolean>(window.location.pathname === "/free-trial");
  const [error, setError] = useState<string>('');

  const validationSchema = !freeTrialPage ? 
    Yup.object({
      firstName: Yup.string().required('Please enter your first name'),
      lastName: Yup.string().required('Please enter your last name'),
      emailAddress: Yup.string()
        .required('Please enter your email')
        .email('Please enter a valid email'),
      password: Yup.string()
        .required('Password is required')
        .min(8, PASSWORD_MATCH_POLICY_ERROR_MESSAGE)
        .max(20, PASSWORD_MATCH_POLICY_ERROR_MESSAGE)
        .matches(PASSWORD_REGEX, PASSWORD_MATCH_POLICY_ERROR_MESSAGE),
      confirmPassword: Yup.string().test(
        'equal',
        `Passwords don't match`,
        function (v) {
          return v === this.resolve(Yup.ref('password'));
        }
      ),
      schoolOfficeFirstName: Yup.string().required('Please enter your school office first name'),
      schoolOfficeLastName: Yup.string().required('Please enter your school office last name'),
      schoolOfficeEmailAddress: Yup.string()
        .required('Please enter your school office email')
        .email('Please enter a valid school office email')
        .test(
          'equal',
          `School office email cannot be the same as your regular email`,
          function (v) {
            return v !== this.resolve(Yup.ref('emailAddress'));
          }
        ),    
      bAcceptedTermsAndConditions: Yup.bool().oneOf([true], 'You need to accept the terms and conditions'),     
    })
  :
    Yup.object({
      firstName: Yup.string().required('Please enter your first name'),
      lastName: Yup.string().required('Please enter your last name'),
      emailAddress: Yup.string()
        .required('Please enter your email')
        .email('Please enter a valid email'),
      password: Yup.string()
        .required('Password is required')
        .min(8, PASSWORD_MATCH_POLICY_ERROR_MESSAGE)
        .max(20, PASSWORD_MATCH_POLICY_ERROR_MESSAGE)
        .matches(PASSWORD_REGEX, PASSWORD_MATCH_POLICY_ERROR_MESSAGE),
      confirmPassword: Yup.string().test(
        'equal',
        `Passwords don't match`,
        function (v) {
          return v === this.resolve(Yup.ref('password'));
        }
      ),
      bAcceptedTermsAndConditions: Yup.bool().oneOf([true], 'You need to accept the terms and conditions'),     
    })
  ;

  const handleSubmitForm = async (
    values: any,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    if (!schoolDetails) {
      setError('Sorry, something went wrong. Please try again.');
      setSubmitting(false);
      return;
    }

    const result = !freeTrialPage ? await SubscribeApi.createSchoolUser(values, schoolDetails) : await SubscribeApi.createSchoolUserFreeTrial(values, schoolDetails);
    
    if (result.data) {
      const registrationToken = result.data.registartionToken;
      if (pathName === AppPath.SUBSCRIBE_FREE_TRIAL) {
        const result1 = await FreeTrialSubscriptionApi.submitFreeTrialPackages(
          selectedPackages,
          registrationToken
        );

        if (result1.error) {
          setError(result.error.message);
          return;
        }
        onSubmit();
      }
      if (pathName === AppPath.SUBSCRIBE) {
        dispatch(setRegistrationToken(registrationToken));
        onSubmit();
      }
    }
    if (result.error) {
      setError(result.error.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="Form">
      <FormGeneralError message={error} />
      <Formik
        initialValues={!freeTrialPage ? SubscribeParser.createEmptySchoolUser() : SubscribeParser.createEmptySchoolUserFreeTrial()}
        onSubmit={(values, formikHelpers) =>
          handleSubmitForm(values, formikHelpers)
        }
        validationSchema={validationSchema}
      >
        {({ 
          setFieldValue,
          values,
          isSubmitting 
        }) => {
          return (
            <Form>
              <h4>Lead contact</h4>
              <div className="SideBySide">
                <FormTextField
                  field={'firstName'}
                  label={'First name'}
                  required={true}
                />
                <FormTextField
                  field={'lastName'}
                  label={'Last name'}
                  required={true}
                />
              </div>
              <FormTextField
                field={'emailAddress'}
                label={'Email address'}
                required={true}
              />
              <FormTextField
                field={'password'}
                label={'Password'}
                required={true}
                type={'password'}
              />
              <FormTextField
                field={'confirmPassword'}
                label={'Confirm Password'}
                required={true}
                type={'password'}
              />
              {!freeTrialPage &&
                <>
                  <br />
                  <h4>Second contact</h4>
                  <FormTextField
                    field={'schoolOfficeFirstName'}
                    label={'First name'}
                    required={true}
                  />
                  <FormTextField
                    field={'schoolOfficeLastName'}
                    label={'Last name'}
                    required={true}
                  />
                  <FormTextField
                    field={'schoolOfficeEmailAddress'}
                    label={'Email address'}
                    required={true}
                  />
                </>
              }             
              <div className="TandCBoxRow">
                <div className="CheckboxBlock">
                  <input 
                    id="bAcceptedTermsAndConditions"
                    name="bAcceptedTermsAndConditions"
                    type="checkbox"
                    checked={values.bAcceptedTermsAndConditions}
                    onChange={() => {
                      setFieldValue("bAcceptedTermsAndConditions", !values.bAcceptedTermsAndConditions);
                    }}
                  />
                  <label className="CustomInput" htmlFor={"bAcceptedTermsAndConditions"}></label>
                </div>
                <div className="TxtBlock">
                  Accept <a href={`${PUBLIC_SITE_URL}/info/terms-and-conditions`} aria-label="Go to Terms and conditions page" target="_blank">Terms and Conditions</a> for your school
                </div>
              </div>
              <ErrorM name={"bAcceptedTermsAndConditions"} />
              <div style={{ marginBottom: "20px" }}></div>
              <button
                className="Btn BtnOrange"
                type="submit"
                disabled={isSubmitting}
              >
                Save and continue
              </button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ProfileDetailsForm;
